/**
 * Core DataTable Component
 *
 * Enterprise-grade data table with sortable columns, row selection,
 * pagination, loading state, empty state, expandable rows, bulk actions,
 * sticky header, and column visibility toggling.
 *
 * This is a lightweight headless table — for heavy grids use AG Grid or MUI X DataGrid directly.
 *
 * @example
 *   <DataTable
 *     columns={[{ key: 'name', header: 'Name', sortable: true }, { key: 'status', header: 'Status' }]}
 *     data={users}
 *     selectable
 *     onSelectionChange={setSelected}
 *     pagination={{ page: 1, pageSize: 20, total: 200, onPageChange, onPageSizeChange }}
 *   />
 */
import React, { useState, useMemo, useCallback } from 'react';
import { Box, Typography, IconButton, Checkbox as MuiCheckbox, CircularProgress } from '@mui/material';
import { ChevronUp, ChevronDown, ChevronsUpDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

// ── Column definition ───────────────────────────────────────────────────
export interface DataTableColumn<T = any> {
  /** Unique key — also used as accessor for simple data[key] */
  key: string;
  /** Column header */
  header: React.ReactNode;
  /** Custom cell renderer */
  render?: (row: T, rowIndex: number) => React.ReactNode;
  /** Enable sorting on this column */
  sortable?: boolean;
  /** Column width (CSS value) */
  width?: string | number;
  /** Min width */
  minWidth?: number;
  /** Alignment */
  align?: 'left' | 'center' | 'right';
  /** Hide this column */
  hidden?: boolean;
  /** Sticky column */
  sticky?: 'left' | 'right';
  /** Tooltip on header */
  headerTooltip?: string;
}

// ── Sort state ──────────────────────────────────────────────────────────
export interface SortState {
  key: string;
  direction: 'asc' | 'desc';
}

// ── Pagination state ────────────────────────────────────────────────────
export interface DataTablePagination {
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  pageSizeOptions?: number[];
}

// ── Props ───────────────────────────────────────────────────────────────
export interface DataTableProps<T = any> {
  columns: DataTableColumn<T>[];
  data: T[];

  /** Unique key accessor for each row */
  rowKey?: string | ((row: T, idx: number) => string);

  /* ── Sorting ────────────────────────────────────────────────── */
  sort?: SortState;
  onSortChange?: (sort: SortState) => void;
  /** Enable client-side sorting (if false, only fires onSortChange for server-side) */
  clientSort?: boolean;

  /* ── Selection ──────────────────────────────────────────────── */
  selectable?: boolean;
  selectedKeys?: string[];
  onSelectionChange?: (keys: string[]) => void;

  /* ── Pagination ─────────────────────────────────────────────── */
  pagination?: DataTablePagination;

  /* ── Expandable rows ────────────────────────────────────────── */
  expandable?: (row: T) => React.ReactNode;

  /* ── States ─────────────────────────────────────────────────── */
  loading?: boolean;
  emptyTitle?: string;
  emptyDescription?: string;
  emptyAction?: React.ReactNode;

  /* ── Visual ─────────────────────────────────────────────────── */
  /** Compact row height */
  compact?: boolean;
  /** Striped rows */
  striped?: boolean;
  /** Bordered cells */
  bordered?: boolean;
  /** Sticky header */
  stickyHeader?: boolean;
  /** Max height for scroll container */
  maxHeight?: string | number;
  /** Row click handler */
  onRowClick?: (row: T, index: number) => void;
  /** Row class name fn */
  rowClassName?: (row: T, index: number) => string;
  /** Header extra content (right side) */
  headerExtra?: React.ReactNode;
  className?: string;
}

// ── Helper: get row key ─────────────────────────────────────────────────
function getKey<T>(row: T, idx: number, rowKey?: string | ((row: T, idx: number) => string)): string {
  if (!rowKey) return String(idx);
  if (typeof rowKey === 'function') return rowKey(row, idx);
  return String((row as any)[rowKey] ?? idx);
}

// ── Component ───────────────────────────────────────────────────────────
export function DataTable<T = any>({
  columns,
  data,
  rowKey,
  sort,
  onSortChange,
  clientSort = false,
  selectable = false,
  selectedKeys = [],
  onSelectionChange,
  pagination,
  expandable,
  loading = false,
  emptyTitle = 'No data',
  emptyDescription,
  emptyAction,
  compact = false,
  striped = false,
  bordered = false,
  stickyHeader = true,
  maxHeight,
  onRowClick,
  rowClassName,
  headerExtra,
  className = '',
}: DataTableProps<T>) {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [internalSort, setInternalSort] = useState<SortState | undefined>(sort);

  const activeSort = sort ?? internalSort;

  const visibleColumns = columns.filter((c) => !c.hidden);

  // ── Client-side sort ──────────────────────────────────────────
  const sortedData = useMemo(() => {
    if (!clientSort || !activeSort) return data;
    const col = columns.find((c) => c.key === activeSort.key);
    if (!col) return data;
    return [...data].sort((a, b) => {
      const aVal = (a as any)[activeSort.key];
      const bVal = (b as any)[activeSort.key];
      if (aVal == null) return 1;
      if (bVal == null) return -1;
      const cmp = String(aVal).localeCompare(String(bVal), undefined, { numeric: true });
      return activeSort.direction === 'asc' ? cmp : -cmp;
    });
  }, [data, activeSort, clientSort, columns]);

  // ── Handlers ──────────────────────────────────────────────────
  const handleSort = useCallback((key: string) => {
    const next: SortState = activeSort?.key === key && activeSort.direction === 'asc'
      ? { key, direction: 'desc' }
      : { key, direction: 'asc' };
    if (!sort) setInternalSort(next);
    onSortChange?.(next);
  }, [activeSort, sort, onSortChange]);

  const allKeys = sortedData.map((row, i) => getKey(row, i, rowKey));
  const allSelected = allKeys.length > 0 && allKeys.every((k) => selectedKeys.includes(k));

  const toggleSelectAll = () => {
    onSelectionChange?.(allSelected ? [] : allKeys);
  };

  const toggleSelect = (key: string) => {
    onSelectionChange?.(
      selectedKeys.includes(key) ? selectedKeys.filter((k) => k !== key) : [...selectedKeys, key],
    );
  };

  const toggleExpand = (key: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const cellPadding = compact ? 'px-3 py-1.5' : 'px-4 py-3';
  const totalCols = visibleColumns.length + (selectable ? 1 : 0) + (expandable ? 1 : 0);

  return (
    <div className={cn('rounded-xl border border-gray-100 dark:border-slate-800 overflow-hidden bg-white dark:bg-slate-900', className)}>
      <div
        className="overflow-auto"
        style={{ maxHeight: maxHeight || undefined }}
      >
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className={cn(
              'bg-slate-50 dark:bg-slate-800/60',
              stickyHeader && 'sticky top-0 z-10',
            )}>
              {selectable && (
                <th className={cn('w-10 text-center', cellPadding)}>
                  <MuiCheckbox
                    size="small"
                    checked={allSelected}
                    indeterminate={selectedKeys.length > 0 && !allSelected}
                    onChange={toggleSelectAll}
                  />
                </th>
              )}
              {expandable && <th className={cn('w-10', cellPadding)} />}
              {visibleColumns.map((col) => (
                <th
                  key={col.key}
                  className={cn(
                    cellPadding,
                    'text-left font-semibold text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wider select-none',
                    col.sortable && 'cursor-pointer hover:text-slate-700 dark:hover:text-slate-200',
                    bordered && 'border-r border-gray-100 dark:border-slate-800 last:border-r-0',
                  )}
                  style={{ width: col.width, minWidth: col.minWidth, textAlign: col.align }}
                  onClick={col.sortable ? () => handleSort(col.key) : undefined}
                >
                  <span className="inline-flex items-center gap-1">
                    {col.header}
                    {col.sortable && (
                      activeSort?.key === col.key ? (
                        activeSort.direction === 'asc'
                          ? <ChevronUp className="h-3 w-3" />
                          : <ChevronDown className="h-3 w-3" />
                      ) : (
                        <ChevronsUpDown className="h-3 w-3 opacity-30" />
                      )
                    )}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={totalCols} className="text-center py-16">
                  <CircularProgress size={28} />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Loading data…</Typography>
                </td>
              </tr>
            ) : sortedData.length === 0 ? (
              <tr>
                <td colSpan={totalCols} className="text-center py-16">
                  <Typography variant="body2" fontWeight={600} color="text.secondary">{emptyTitle}</Typography>
                  {emptyDescription && <Typography variant="caption" color="text.secondary">{emptyDescription}</Typography>}
                  {emptyAction && <div className="mt-3">{emptyAction}</div>}
                </td>
              </tr>
            ) : (
              sortedData.map((row, idx) => {
                const key = getKey(row, idx, rowKey);
                const isExpanded = expandedRows.has(key);
                return (
                  <React.Fragment key={key}>
                    <tr
                      className={cn(
                        'border-t border-gray-50 dark:border-slate-800/50 transition-colors',
                        striped && idx % 2 === 1 && 'bg-slate-50/50 dark:bg-slate-800/20',
                        onRowClick && 'cursor-pointer hover:bg-blue-50/40 dark:hover:bg-blue-900/10',
                        selectedKeys.includes(key) && 'bg-blue-50 dark:bg-blue-900/15',
                        rowClassName?.(row, idx),
                      )}
                      onClick={onRowClick ? () => onRowClick(row, idx) : undefined}
                    >
                      {selectable && (
                        <td className={cn('text-center', cellPadding)} onClick={(e) => e.stopPropagation()}>
                          <MuiCheckbox size="small" checked={selectedKeys.includes(key)} onChange={() => toggleSelect(key)} />
                        </td>
                      )}
                      {expandable && (
                        <td className={cellPadding} onClick={(e) => e.stopPropagation()}>
                          <IconButton size="small" onClick={() => toggleExpand(key)}>
                            {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                          </IconButton>
                        </td>
                      )}
                      {visibleColumns.map((col) => (
                        <td
                          key={col.key}
                          className={cn(
                            cellPadding,
                            'text-slate-700 dark:text-slate-300',
                            bordered && 'border-r border-gray-50 dark:border-slate-800 last:border-r-0',
                          )}
                          style={{ textAlign: col.align }}
                        >
                          {col.render ? col.render(row, idx) : (row as any)[col.key]}
                        </td>
                      ))}
                    </tr>
                    {expandable && isExpanded && (
                      <tr className="bg-slate-50/50 dark:bg-slate-800/30">
                        <td colSpan={totalCols} className="px-6 py-4">
                          {expandable(row)}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* ── Pagination footer ── */}
      {pagination && (
        <div className="flex items-center justify-between px-4 py-2.5 border-t border-gray-100 dark:border-slate-800 bg-white dark:bg-slate-900">
          <div className="flex items-center gap-2 text-xs text-slate-500">
            {pagination.onPageSizeChange && (
              <select
                value={pagination.pageSize}
                onChange={(e) => pagination.onPageSizeChange?.(Number(e.target.value))}
                className="bg-transparent border border-slate-200 dark:border-slate-700 rounded px-1.5 py-0.5 text-xs"
              >
                {(pagination.pageSizeOptions || [10, 20, 50, 100]).map((s) => (
                  <option key={s} value={s}>{s} / page</option>
                ))}
              </select>
            )}
            <span>
              {((pagination.page - 1) * pagination.pageSize) + 1}–{Math.min(pagination.page * pagination.pageSize, pagination.total)} of {pagination.total}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <IconButton
              size="small"
              disabled={pagination.page <= 1}
              onClick={() => pagination.onPageChange(pagination.page - 1)}
            >
              <ChevronLeft className="h-4 w-4" />
            </IconButton>
            <span className="text-xs font-medium px-2">
              Page {pagination.page} of {Math.ceil(pagination.total / pagination.pageSize)}
            </span>
            <IconButton
              size="small"
              disabled={pagination.page >= Math.ceil(pagination.total / pagination.pageSize)}
              onClick={() => pagination.onPageChange(pagination.page + 1)}
            >
              <ChevronRight className="h-4 w-4" />
            </IconButton>
          </div>
        </div>
      )}
    </div>
  );
}

export default DataTable;
